import { useState } from 'react';
import { Loader2, UserPlus, Check } from 'lucide-react';
import type { PersonPromptDecision } from '../../../lib/mail/threadLink';
import type { PersonValues } from '../../../lib/mail/createFromProspect';

interface Props {
  decision: Exclude<PersonPromptDecision, 'none'>;
  companyName: string;
  person: PersonValues;
  onPersonChange: (field: keyof PersonValues, value: string) => void;
  /** Nome della persona gia' in anagrafica, mostrato nel caso "collega soltanto". */
  existingPersonName: string | null;
  busy: boolean;
  onConfirm: () => void;
  onSkip: () => void;
}

const FIELDS: { key: keyof PersonValues; label: string; type?: string }[] = [
  { key: 'business_name', label: 'Nome e cognome' },
  { key: 'email', label: 'Email', type: 'email' },
  { key: 'phone', label: 'Telefono' },
  { key: 'contact_role', label: 'Ruolo' },
];

/**
 * Compare dopo aver collegato il thread a un'azienda gia' in anagrafica: chi scrive
 * e' una persona, e finora non c'era modo di censirla senza uscire dal pannello.
 * I campi arrivano precompilati dalla firma dell'email.
 */
export function PersonOnCompanyPrompt({
  decision, companyName, person, onPersonChange, existingPersonName, busy, onConfirm, onSkip,
}: Props) {
  const [expanded, setExpanded] = useState(decision === 'create');

  return (
    <div className="space-y-3 mail-link-section">
      <div className="rounded-xl border border-[#2383E2]/25 bg-[rgba(35,131,226,0.06)] p-3">
        <div className="flex items-start gap-2">
          <Check className="h-4 w-4 text-[#2383E2] flex-shrink-0 mt-0.5" />
          <p className="text-xs text-[#37352F] dark:text-white">
            Thread collegato a <span className="font-medium">{companyName}</span>.
          </p>
        </div>
      </div>

      <div className="rounded-xl border border-black/[0.08] dark:border-gray-700 p-3 space-y-3">
        <div className="flex items-center gap-2">
          <UserPlus className="h-4 w-4 text-[#787774] flex-shrink-0" />
          <p className="text-xs text-[#37352F] dark:text-white">
            {decision === 'link'
              ? <>
                  <span className="font-medium">{existingPersonName}</span> e' gia' in anagrafica
                  ma non risulta collegata a {companyName}. Collegarla come referente?
                </>
              : <>Chi scrive non e' in anagrafica. Crearla come referente di {companyName}?</>}
          </p>
        </div>

        {decision === 'create' && (
          <>
            {!expanded && (
              <button
                onClick={() => setExpanded(true)}
                className="text-xs text-[#2383E2] hover:underline"
              >
                Modifica i dati
              </button>
            )}
            {expanded && (
              <div className="space-y-2 mail-link-section">
                {FIELDS.map(f => (
                  <div key={f.key}>
                    <label className="block text-[11px] font-medium text-[#787774] dark:text-white/50 mb-0.5">
                      {f.label}
                    </label>
                    <input
                      type={f.type || 'text'}
                      value={person[f.key]}
                      onChange={e => onPersonChange(f.key, e.target.value)}
                      className="w-full px-2.5 py-1.5 text-sm bg-[rgba(55,53,47,0.04)] dark:bg-white/5 border border-transparent rounded-md transition-all duration-150 focus:outline-none focus:bg-white dark:focus:bg-gray-800 focus:border-[#2383E2] focus:ring-2 focus:ring-[#2383E2]/15 text-[#37352F] dark:text-white"
                    />
                  </div>
                ))}
              </div>
            )}
          </>
        )}

        <div className="flex items-center justify-end gap-2 pt-1">
          <button
            onClick={onSkip}
            disabled={busy}
            className="px-3 py-1.5 text-xs text-[#787774] hover:text-[#37352F] dark:hover:text-white transition-colors disabled:opacity-50"
          >
            No, grazie
          </button>
          <button
            onClick={onConfirm}
            disabled={busy}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-[#2383E2] text-white text-xs font-medium rounded-md shadow-sm shadow-[#2383E2]/25 hover:bg-[#1a6fc9] active:scale-[0.98] transition-all duration-100 disabled:opacity-50 disabled:active:scale-100"
          >
            {busy ? <Loader2 className="h-3 w-3 animate-spin" /> : <Check className="h-3 w-3" />}
            {decision === 'link' ? 'Collega la persona' : 'Crea la persona'}
          </button>
        </div>
      </div>
    </div>
  );
}
